import React, { useEffect } from 'react';

import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';

import ErrorPage from './pages/ErrorPage/ErrorPage';
import AppRoutes from './routes/AppRoutes';
import ScrollToTop from './shared/components/v3/ScrollToTop/ScrollToTop';
import { INTERCOM_APP_ID } from './shared/constants/intercom';
import SessionContextProvider from './shared/context/auth/authContext';
import CompanyContextProvider from './shared/context/company/companyContext';
import ModalContextProvider from './shared/context/modal/modalContext';
import PlansContextProvider from './shared/context/plans/plansContext';
import StatsigCustomProvider from './shared/context/statsig/statsigContext';
import { authInterceptor } from './shared/interceptors/auth/auth.interceptor';
import { theme } from './theme/materialTheme';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    grecaptcha: any;
    captchaOnLoad: () => void;
  }
}

const App = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('csp.site_title');
  }, [t]);

  useEffect(() => {
    authInterceptor();
  }, []);

  return (
    <Router>
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <ModalContextProvider>
              <IntercomProvider appId={INTERCOM_APP_ID}>
                <SessionContextProvider>
                  <CompanyContextProvider>
                    <PlansContextProvider>
                      <StatsigCustomProvider>
                        <ScrollToTop />
                        <AppRoutes />
                      </StatsigCustomProvider>
                    </PlansContextProvider>
                  </CompanyContextProvider>
                </SessionContextProvider>
              </IntercomProvider>
            </ModalContextProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
